import { useParams, Link } from 'react-router-dom';
import { useState } from 'react';
import Navbar from '@/components/feature/Navbar';
import Footer from '@/components/feature/Footer';
import Breadcrumb from '@/components/feature/Breadcrumb';
import ScrollToTop from '@/components/feature/ScrollToTop';
import VideoCard from '@/components/feature/VideoCard';
import { allVideos } from '@/mocks/videos';
import { articles } from '@/mocks/articles';

export default function Article() {
  const { id } = useParams<{ id: string }>();
  const itemId = Number(id);

  const article = articles.find((a) => a.id === itemId);
  const video = allVideos.find((v) => v.id === itemId);

  const [copied, setCopied] = useState(false);
  const handleShare = () => {
    navigator.clipboard.writeText(window.location.href).then(() => {
      setCopied(true);
      setTimeout(() => setCopied(false), 2000);
    }).catch(() => {});
  };

  if (!article && !video) {
    return (
      <>
        <ScrollToTop />
        <Navbar />
        <main className="pt-24 pb-12 px-4 md:px-6 min-h-screen flex items-center justify-center">
          <div className="text-center">
            <div className="w-16 h-16 flex items-center justify-center mx-auto rounded-full bg-background-100 mb-4">
              <i className="ri-error-warning-line text-2xl text-foreground-500"></i>
            </div>
            <h2 className="text-lg font-medium text-foreground-300 mb-2">内容不存在</h2>
            <Link to="/" className="text-sm text-accent-500 hover:text-accent-400 transition-colors cursor-pointer">
              返回首页
            </Link>
          </div>
        </main>
        <Footer />
      </>
    );
  }

  const formatViews = (n: number) => {
    if (n >= 10000) return `${(n / 10000).toFixed(1)}万`;
    return n.toLocaleString();
  };

  const isArticle = !!article;
  const entity = article || video!;

  const relatedVideos = allVideos
    .filter((v) => v.category === entity.category && v.id !== itemId)
    .slice(0, 4);

  const relatedArticles = articles
    .filter((a) => a.id !== itemId)
    .slice(0, 3);

  return (
    <>
      <ScrollToTop />
      <Navbar />

      <main className="pt-20 pb-12">
        <article className="px-4 md:px-6">
          <div className="max-w-4xl mx-auto">
            <Breadcrumb
              items={[
                { label: isArticle ? '精选文章' : '视频详情', href: isArticle ? '/' : '/category/all' },
                { label: entity.title },
              ]}
            />

            {/* Header */}
            <header className="mb-8">
              <div className="flex flex-wrap gap-2 mb-4">
                {entity.tags.map((tag: string) => (
                  <span
                    key={tag}
                    className="px-3 py-1 rounded-full text-xs bg-accent-500/10 text-accent-500 border border-accent-500/20"
                  >
                    {tag}
                  </span>
                ))}
              </div>

              <h1 className="text-2xl md:text-4xl font-bold font-heading text-foreground-100 mb-4 leading-tight">
                {entity.title}
              </h1>

              {isArticle ? (
                <div className="flex flex-wrap items-center gap-4 text-sm text-foreground-500 mb-6">
                  <span className="flex items-center gap-1.5">
                    <i className="ri-user-line"></i>
                    {article!.author}
                  </span>
                  <span className="flex items-center gap-1.5">
                    <i className="ri-calendar-line"></i>
                    {article!.publishDate}
                  </span>
                  <span className="flex items-center gap-1.5">
                    <i className="ri-time-line"></i>
                    阅读 {article!.readTime}
                  </span>
                  <button
                    onClick={handleShare}
                    className="flex items-center gap-1.5 text-accent-500 hover:text-accent-400 transition-colors cursor-pointer ml-auto"
                  >
                    <i className={copied ? 'ri-check-line' : 'ri-share-forward-line'}></i>
                    {copied ? '已复制' : '分享'}
                  </button>
                </div>
              ) : (
                <div className="flex flex-wrap items-center gap-4 text-sm text-foreground-500 mb-6">
                  <span className="px-2 py-0.5 rounded text-xs font-medium bg-primary-500 text-background-50">
                    {video!.quality}
                  </span>
                  <span className="flex items-center gap-1.5">
                    <i className="ri-time-line"></i>
                    {video!.duration}
                  </span>
                  <span className="flex items-center gap-1.5">
                    <i className="ri-calendar-line"></i>
                    {video!.releaseDate}
                  </span>
                  <span className="flex items-center gap-1.5">
                    <i className="ri-eye-line"></i>
                    {formatViews(video!.views)} 次观看
                  </span>
                  <span className="flex items-center gap-1.5">
                    <i className="ri-star-fill text-accent-500"></i>
                    {video!.rating}
                  </span>
                  <button
                    onClick={handleShare}
                    className="flex items-center gap-1.5 text-accent-500 hover:text-accent-400 transition-colors cursor-pointer ml-auto"
                  >
                    <i className={copied ? 'ri-check-line' : 'ri-share-forward-line'}></i>
                    {copied ? '已复制' : '分享'}
                  </button>
                </div>
              )}

              {/* Cover / Video Player Area */}
              <div className="aspect-video rounded-xl overflow-hidden bg-background-200 mb-8 relative">
                <img
                  src={entity.cover}
                  alt={entity.title}
                  className="w-full h-full object-cover"
                  title={entity.title}
                />
                {!isArticle && (
                  <div className="absolute inset-0 flex items-center justify-center bg-background-950/40">
                    <button className="w-16 h-16 md:w-20 md:h-20 flex items-center justify-center rounded-full bg-accent-500/90 hover:bg-accent-500 transition-all hover:scale-105 cursor-pointer">
                      <i className="ri-play-fill text-foreground-50 text-2xl md:text-3xl ml-1"></i>
                    </button>
                  </div>
                )}
              </div>
            </header>

            {/* Content */}
            {isArticle ? (
              <div
                className="prose prose-invert max-w-none mb-12
                  prose-p:text-foreground-200 prose-p:leading-relaxed prose-p:mb-4 prose-p:text-sm md:prose-p:text-base
                  prose-strong:text-foreground-100 prose-strong:font-semibold
                  prose-h2:text-xl prose-h2:font-bold prose-h2:text-foreground-100 prose-h2:mt-8 prose-h2:mb-4
                  prose-h3:text-lg prose-h3:font-semibold prose-h3:text-foreground-100 prose-h3:mt-6 prose-h3:mb-3"
                dangerouslySetInnerHTML={{ __html: article!.content }}
              />
            ) : (
              <div className="mb-12">
                <div className="p-6 rounded-xl bg-background-100 border border-background-200/50 mb-6">
                  <h3 className="text-lg font-semibold text-foreground-100 mb-3 font-heading">影片简介</h3>
                  <p className="text-sm md:text-base text-foreground-200 leading-relaxed">
                    {video!.description}
                  </p>
                </div>

                <div className="grid grid-cols-2 md:grid-cols-4 gap-4">
                  <div className="p-4 rounded-lg bg-background-100 border border-background-200/50 text-center">
                    <div className="text-xs text-foreground-500 mb-1">画质</div>
                    <div className="text-sm font-semibold text-accent-500">{video!.quality}</div>
                  </div>
                  <div className="p-4 rounded-lg bg-background-100 border border-background-200/50 text-center">
                    <div className="text-xs text-foreground-500 mb-1">时长</div>
                    <div className="text-sm font-semibold text-foreground-100">{video!.duration}</div>
                  </div>
                  <div className="p-4 rounded-lg bg-background-100 border border-background-200/50 text-center">
                    <div className="text-xs text-foreground-500 mb-1">上映日期</div>
                    <div className="text-sm font-semibold text-foreground-100">{video!.releaseDate}</div>
                  </div>
                  <div className="p-4 rounded-lg bg-background-100 border border-background-200/50 text-center">
                    <div className="text-xs text-foreground-500 mb-1">评分</div>
                    <div className="text-sm font-semibold text-accent-500">{video!.rating} / 10</div>
                  </div>
                </div>
              </div>
            )}

            {/* Related Videos */}
            {relatedVideos.length > 0 && (
              <section className="mb-12 pt-8 border-t border-background-200">
                <h3 className="text-lg font-bold font-heading text-foreground-100 mb-6">
                  相关视频推荐
                </h3>
                <div className="grid grid-cols-2 md:grid-cols-4 gap-4">
                  {relatedVideos.map((v) => (
                    <VideoCard key={v.id} video={v} />
                  ))}
                </div>
              </section>
            )}

            {/* Related Articles */}
            {relatedArticles.length > 0 && (
              <section className="pt-8 border-t border-background-200">
                <h3 className="text-lg font-bold font-heading text-foreground-100 mb-6">
                  相关文章推荐
                </h3>
                <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
                  {relatedArticles.map((ra) => (
                    <Link
                      key={ra.id}
                      to={`/article/${ra.id}`}
                      className="group p-4 rounded-lg bg-background-100 hover:bg-background-200 transition-colors cursor-pointer"
                    >
                      <div className="aspect-video rounded-lg overflow-hidden bg-background-200 mb-3">
                        <img
                          src={ra.cover}
                          alt={ra.title}
                          className="w-full h-full object-cover group-hover:scale-105 transition-transform duration-500"
                          title={ra.title}
                        />
                      </div>
                      <h4 className="text-sm font-medium text-foreground-100 group-hover:text-accent-500 transition-colors line-clamp-2 mb-2">
                        {ra.title}
                      </h4>
                      <span className="text-xs text-foreground-500">{ra.publishDate}</span>
                    </Link>
                  ))}
                </div>
              </section>
            )}
          </div>
        </article>
      </main>

      <Footer />

      <script
        type="application/ld+json"
        dangerouslySetInnerHTML={{
          __html: JSON.stringify(
            isArticle
              ? {
                  '@context': 'https://schema.org',
                  '@type': 'Article',
                  headline: article!.title,
                  description: article!.excerpt,
                  image: article!.cover,
                  author: { '@type': 'Person', name: article!.author },
                  datePublished: article!.publishDate,
                  publisher: { '@type': 'Organization', name: '51视频', url: 'https://51video.com/' },
                  url: `https://51video.com/article/${article!.id}`,
                }
              : {
                  '@context': 'https://schema.org',
                  '@type': 'Movie',
                  name: video!.title,
                  description: video!.description,
                  image: video!.cover,
                  dateCreated: video!.releaseDate,
                  url: `https://51video.com/article/${video!.id}`,
                }
          ),
        }}
      />
      <script
        type="application/ld+json"
        dangerouslySetInnerHTML={{
          __html: JSON.stringify({
            '@context': 'https://schema.org',
            '@type': 'BreadcrumbList',
            itemListElement: [
              { '@type': 'ListItem', position: 1, name: '首页', item: 'https://51video.com/' },
              { '@type': 'ListItem', position: 2, name: isArticle ? '精选文章' : '视频详情', item: 'https://51video.com/' },
              { '@type': 'ListItem', position: 3, name: entity.title },
            ],
          }),
        }}
      />
    </>
  );
}